import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useFormik } from 'formik';
import { getCoreRowModel } from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/table-core';

// MUI Theme
import { Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

// Redux
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

// Utils
import { Storage, getRetainState } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Services
import { getDeviceTypes } from '../../../services/deviceType.service';
import { exportDevices, getDevices, updateDeviceLabels } from '../../../services/device.service';

// Hooks
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';
import { useExport } from '../../../hooks/useExport';

// Types
import { DeviceType, DeviceTypeResponse } from '../../../types/DeviceType';
import { ResourceType } from '../../../types/ResourceType';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { SearchBarFilterTypes } from '../../../constants/Constants';

// Common Components
import { Table, useNewTable } from '../../common/table/index';
import CustomModal from '../../common/withModal';
import SearchBar from '../../common/SearchBar/SearchBar';

// Components
import { makeDeviceListColumn } from './makeDeviceListColumn';
import LabelAssignment from '../labels/labelAssignment';
import { DeviceEnriched } from '../../../types/DeviceEnriched';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

interface DevicesProp {
  accessKey?: string;
  facilityId?: string | null;
  facilityName?: string | null;
  // The station the facility belongs to. The stock device endpoint only returns the
  // right rows when it gets the station alongside the facility, so the fleet dashboard
  // passes both; callers that filter by facility alone leave this unset.
  stationIataCode?: string | null;
  onClearFacility?: () => void;
  hideSearchBar?: boolean;
  title?: string;
  hideToolbarActions?: boolean;
  fetchAsync?: typeof getDevices;
  // Lets an embedded instance (e.g. the fleet dashboard's Device Details) show a
  // different column set; the standalone Devices page keeps makeDeviceListColumn.
  columns?: ColumnDef<DeviceEnriched>[];
}

type SuperSentinelFilter = 'active' | 'inactive' | 'not_supported' | undefined;

export default function Devices(props: DevicesProp): JSX.Element {
  const { notifyMessage } = useNotifyMessage();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedLabelItem, setSelectedLabelItem] = useState<any>(null);
  const [deviceType, setDeviceType] = useState<number | undefined>();
  const [manufacturer, setManufacturer] = useState<string | undefined>();
  const [manufactureDateTimeFrom, setManufactureDateTimeFrom] = useState<Date | null>(null);
  const [manufactureDateTimeTo, setManufactureDateTimeTo] = useState<Date | null>(null);
  const [superSentinel, setSuperSentinel] = useState<SuperSentinelFilter>(undefined);
  // Filters the user picked (search bar + dropdowns). The facility scope coming
  // from props is merged on top of these, but deliberately never persisted.
  const [userFilters, setUserFilters] = useState<any[]>([]);
  const [changedAt, setChangedAt] = useState<Date | undefined>();
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [filterVersion, setFilterVersion] = useState<number>(0);

  const dispatch = useAppDispatch();
  const settingStates = useAppSelector((state) => state.settings);

  const { isExporting, startExport } = useExport('Device');

  const handleDetail = (device) => {
    alert(device.deviceId);
  };

  const intialDefaultFilters = () => {
    // Embedded instances (e.g. the fleet dashboard's Device Details) hide the search bar,
    // so there is no Ui to see or clear a restored filter.. reapplying the main Devices
    // page's persisted filters there would silently narrow the facility-scoped list, so
    // the restore is skipped and the facility scope from props is the only filter.
    if (props.hideSearchBar) return;

    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.DEVICES);

    getRetainState(SearchBarFilterTypes.DEVICES, 'manufacturer', setManufacturer);
    getRetainState(SearchBarFilterTypes.DEVICES, 'typeId', setDeviceType);
    getRetainState(SearchBarFilterTypes.DEVICES, 'manufactureDateTime', setManufactureDateTimeFrom, true, FilterableTypes.GreaterThanOrEqual);
    getRetainState(SearchBarFilterTypes.DEVICES, 'manufactureDateTime', setManufactureDateTimeTo, true, FilterableTypes.LessThanOrEqual);

    if (filterState?.filters) {
      const sentinelFilter = filterState.filters.find((f) => 'isSuperSentinelSupported' in f);
      if (sentinelFilter) {
        if (sentinelFilter.isSuperSentinelSupported === false) {
          setSuperSentinel('not_supported');
        } else if (sentinelFilter.and?.isSuperSentinelEnabled === true) {
          setSuperSentinel('active');
        } else {
          setSuperSentinel('inactive');
        }
      }
    }

    if (filterState) {
      setUserFilters(filterState.filters ?? []);
    }
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
    intialDefaultFilters();
  }, []);

  useEffect(() => {
    getDeviceTypes().then((result) => {
      if (result.status === 200) {
        const response = result.data as unknown as DeviceTypeResponse;
        setDeviceTypes(response.results);
      }
    });
  }, []);

  const facilityFilters = useMemo(
    () => [
      ...(props.facilityId ? [{ closestFacilityId: props.facilityId, filterType: FilterableTypes.Equals }] : []),
      ...(props.stationIataCode ? [{ stationIataCode: props.stationIataCode, filterType: FilterableTypes.Equals }] : []),
    ],
    [props.facilityId, props.stationIataCode],
  );

  const paramFilter = useMemo(
    () => ({
      entityType: 'Device',
      filters: [...userFilters, ...facilityFilters],
      ...(changedAt ? { changedAt } : {}),
    }),
    [userFilters, facilityFilters, changedAt],
  );

  const columns = useMemo(
    () => props.columns ?? makeDeviceListColumn(handleDetail, deviceTypes, settingStates),
    [props.columns, deviceTypes, settingStates, filterVersion],
  );

  const [listedFacilityName, setListedFacilityName] = useState<string | null>(props.facilityName ?? null);

  const handleFetchComplete = useCallback(() => {
    setListedFacilityName(props.facilityName ?? null);
  }, [props.facilityName]);

  const table = useNewTable<DeviceEnriched>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: props.fetchAsync ?? getDevices,
    exportAsync: exportDevices,
    params: paramFilter,
    serverSide: true,
    onFetchComplete: handleFetchComplete,
  });

  const onSearchChange = (filters: any) => {
    const finalFilters = [...filters];
    if (deviceType !== undefined) {
      finalFilters.push({
        typeId: deviceType,
        filterType: FilterableTypes.Equals,
      });
    }
    if (manufacturer !== undefined) {
      finalFilters.push({
        manufacturer: manufacturer,
        filterType: FilterableTypes.Equals,
      });
    }

    if (superSentinel !== undefined) {
      if (superSentinel === 'not_supported') {
        finalFilters.push({
          isSuperSentinelSupported: false,
          filterType: FilterableTypes.Equals,
        });
      } else {
        finalFilters.push({
          isSuperSentinelSupported: true,
          filterType: FilterableTypes.Equals,
          and: {
            isSuperSentinelEnabled: superSentinel === 'active',
            filterType: FilterableTypes.Equals,
          },
        });
      }
    }

    if (manufactureDateTimeFrom && manufactureDateTimeTo) {
      finalFilters.push({
        manufactureDateTime: FormatForApi(moment(manufactureDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          manufactureDateTime: FormatForApi(moment(manufactureDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    setUserFilters(finalFilters);
    Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICES, { isMoreFilter: true, filters: finalFilters });
  };

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => handleSubmitForm(formik),
  });

  const handleSubmitForm = async (formik) => {
    await updateDeviceLabels({
      entityIds: selectedLabelItem.entityIds,
      labelIds: formik.getFieldProps('labelIds')?.value ?? [],
    }).then((result: any) => {
      if (result.status === 200) {
        setIsEditDialogOpen(false);
        setFilterVersion((v) => v + 1);
        setChangedAt(new Date());
        notifyMessage('Label updated', 'success');
        formik.setFieldValue('labelIds', []);
        return;
      }
      notifyMessage('An error occurred', 'error');
    });
  };

  const manufacturers = _.uniq(deviceTypes.map((type) => type.manufacturer));

  return (
    <React.Fragment>
      {!props.hideSearchBar && (
        <SearchBar
          pageKey={SearchBarFilterTypes.DEVICES}
          onSearch={onSearchChange}
          onReset={() => {
            setDeviceType(undefined);
            setManufacturer(undefined);
            setSuperSentinel(undefined);
            setManufactureDateTimeFrom(null);
            setManufactureDateTimeTo(null);
            setUserFilters([]);
            Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICES, { filters: [] }); // 👈 clears persisted filters so refresh is clean
          }}
          allowSearchAll={true}
          columns={manufacturer === undefined ? columns.filter((c) => 'accessorKey' in c && c.accessorKey !== 'manufacturer') : columns}
          components={
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <FormInputCollection label='Type' style={{ marginRight: '1.5em', marginBottom: '8px' }}>
                  <Select
                    variant='outlined'
                    id='typeId'
                    name='typeId'
                    value={deviceType ?? 'all'}
                    size='small'
                    style={{ width: '220px' }}
                    onChange={(e: any) => {
                      setDeviceType(e.target.value === 'all' ? undefined : e.target.value);
                    }}
                  >
                    <MenuItem value={'all'}>All</MenuItem>
                    {deviceTypes?.map((val, index: number) => {
                      return (
                        <MenuItem key={index} value={val.id}>
                          {val.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormInputCollection>

                <FormInputCollection label='Manufacturer' style={{ marginRight: '1.5em' }}>
                  <Select
                    variant='outlined'
                    id='manufacturer'
                    name='manufacturer'
                    value={manufacturer ?? 'all'}
                    size='small'
                    style={{ width: '200px' }}
                    onChange={(e: any) => {
                      setManufacturer(e.target.value === 'all' ? undefined : e.target.value);
                    }}
                  >
                    <MenuItem value={'all'}>All</MenuItem>
                    {manufacturers.map((val, index: number) => {
                      return (
                        <MenuItem key={index} value={val}>
                          {val}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormInputCollection>

                <FormInputCollection label='Super Sentinel' style={{ marginRight: '1.5em' }}>
                  <Select
                    variant='outlined'
                    id='superSentinel'
                    name='superSentinel'
                    value={superSentinel ?? 'all'}
                    size='small'
                    style={{ width: '160px' }}
                    onChange={(e: any) => {
                      setSuperSentinel(e.target.value === 'all' ? undefined : e.target.value);
                    }}
                  >
                    <MenuItem value='all'>All</MenuItem>
                    <MenuItem value='active'>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#2e7d32', display: 'inline-block' }} />
                        Active
                      </span>
                    </MenuItem>
                    <MenuItem value='inactive'>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#e65100', display: 'inline-block' }} />
                        Inactive
                      </span>
                    </MenuItem>
                    <MenuItem value='not_supported'>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#757575', display: 'inline-block' }} />
                        Not Supported
                      </span>
                    </MenuItem>
                  </Select>
                </FormInputCollection>
                <FormInputCollection label='Manufacture Date'>
                  <div className='searchbar-date-filter'>
                    <div className='searchbar-date-field'>
                      <DatePicker
                        label='From'
                        className='searchbar-datepicker'
                        value={manufactureDateTimeFrom ? moment(manufactureDateTimeFrom) : null}
                        format={shortDateFormat}
                        slotProps={{
                          textField: {
                            size: 'small',
                          },
                        }}
                        onChange={(date) => {
                          setManufactureDateTimeFrom(date ? date.toDate() : null);
                        }}
                      />
                    </div>

                    <div className='searchbar-date-field'>
                      <DatePicker
                        label='To'
                        className='searchbar-datepicker'
                        value={manufactureDateTimeTo ? moment(manufactureDateTimeTo) : null}
                        format={shortDateFormat}
                        slotProps={{
                          textField: {
                            size: 'small',
                          },
                        }}
                        onChange={(date) => {
                          setManufactureDateTimeTo(date ? date.toDate() : null);
                        }}
                      />
                    </div>
                  </div>
                </FormInputCollection>
              </LocalizationProvider>
            </div>
          }
        />
      )}
      <Paper
        {...(props.title || props.facilityName
          ? {
              elevation: 0,
              sx: {
                backgroundColor: '#fff !important',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                '& .ui-table__card': { backgroundColor: '#fff', boxShadow: 'none', border: 'none' },
              },
            }
          : {})}
      >
        {(props.title || props.facilityName) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              py: 1.25,
              backgroundColor: '#fff',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='subtitle1' fontWeight={700}>
                {props.title}
              </Typography>
              {table.loading && <CircularProgress size={14} thickness={5} sx={{ color: '#4338CA' }} />}
            </Box>
            {listedFacilityName && (
              <Chip
                size='small'
                label={`Facility: ${listedFacilityName}`}
                onDelete={props.onClearFacility}
                sx={{ fontWeight: 600, backgroundColor: '#EEF2FF', color: '#4338CA' }}
              />
            )}
          </Box>
        )}
        <CustomModal
          title='Labels'
          isOpen={isEditDialogOpen}
          isValid={formik.isValid}
          setIsOpen={setIsEditDialogOpen}
          onConfirm={formik.handleSubmit}
          maxWidth='xs'
        >
          <LabelAssignment formRef={formik} resourceType={ResourceType.Device} selectedItem={selectedLabelItem} />
        </CustomModal>
        <Table
          {...table}
          isExporting={isExporting}
          showRightAction={!props.hideToolbarActions}
          canExportData={() => !props.hideToolbarActions}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={false}
          showAction={true}
          getCoreRowModel={getCoreRowModel()}
          renderHeaderAction={({ selectedFlatRows }) => {
            return selectedFlatRows.length > 0 ? (
              <div style={{ textAlign: 'right' }}>
                <Box display='flex' justifyContent='flex-end'>
                  <Button
                    variant='contained'
                    color={'error'}
                    size='small'
                    disabled={isEditDialogOpen}
                    style={{ textTransform: 'none' }}
                    onClick={() => {
                      const labelIds: string[] = [];
                      selectedFlatRows.forEach((item) => item.original.labelTypeIds?.forEach((v) => labelIds.push(v)));

                      const uniqLabelIds = _.uniq(labelIds);
                      setSelectedLabelItem({
                        entityIds: selectedFlatRows.map((x) => x.original.id),
                        labelIds: uniqLabelIds,
                      });
                      formik.setFieldValue('labelIds', uniqLabelIds);
                      setIsEditDialogOpen(true);
                    }}
                  >
                    Assign Labels to Device
                  </Button>
                </Box>
              </div>
            ) : null;
          }}
        />
      </Paper>
    </React.Fragment>
  );
}
